import { registerQuoteClock } from './quote-clock';
import { reportPricingFailure, withPricingFailureContext, type PricingEndpoint } from './reporting';
import { ANNUAL_TERMS, PLANS, isValidSubscriptionLength, type PlanTier } from '@/app/config/plans';
import { generateOAuthHeader, getOAuthCredentialsFromSession } from '@/app/utils/oauth';
import {
  PricingApiError,
  type BillingContract,
  type CompletedResult,
  type ExecutionTarget,
  type PricingConfig,
  type PricingQuote,
} from './types';
import {
  isAttemptId,
  isAttemptStatus,
  isBillingContract,
  isConfirmedPurchase,
  isPricingCatalog,
  isPricingConfig,
  isPricingQuote,
  isRecord,
} from './validation';

const responseDates = new WeakMap<object, string | null>();
export function retryAfterSeconds(
  value: string | null,
  serverDate: string | null,
): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > 0) return seconds;
  const delta = Date.parse(value) - Date.parse(serverDate || '');
  return Number.isFinite(delta) && delta > 0 ? Math.ceil(delta / 1000) : undefined;
}
const API_HOST = process.env.NEXT_PUBLIC_API_HOST || '';
export const QUOTE_PATHS: Record<ExecutionTarget, PricingEndpoint> = {
  subscription_create: '/api/pricing/quotes',
  subscription_update: '/api/pricing/subscription/quotes',
  subscription_demote: '/api/pricing/downgrade/quotes',
  payment_method_update: '/api/pricing/payment_method/quotes',
};
export const QUOTED_MEMBERSHIP_PACKAGES: Record<PlanTier, string> = {
  pro: 'Standard',
  promonthly: 'StandardMonthly',
  proplus: 'ProPlus',
  proplusmonthly: 'ProPlusMonthly',
  propluslifetime: 'ProPlusLifetime',
};

const READ_ENDPOINTS: readonly PricingEndpoint[] = [
  '/api/pricing/config', '/api/pricing/catalog', ...Object.values(QUOTE_PATHS),
  '/api/pricing/promo_preview', '/api/pricing/billing_contract', '/api/user/refresh_token',
];
function requestEndpoint(path: string): PricingEndpoint {
  if (READ_ENDPOINTS.includes(path as PricingEndpoint)) return path as PricingEndpoint;
  if (/^\/api\/pricing\/attempts\/[^/?#]+\/result$/.test(path)) return '/api/pricing/attempts/:id/result';
  if (/^\/api\/pricing\/attempts\/[^/?#]+$/.test(path)) return '/api/pricing/attempts/:id';
  return 'unknown';
}

/** Never log response bodies: they can contain accepted quotes or auth credentials. */
export async function pricingRequest(
  path: string,
  body?: Record<string, unknown>,
  authenticated = false,
  apiHost = API_HOST,
): Promise<unknown> {
  const method = body ? 'POST' : 'GET';
  const endpoint = requestEndpoint(path);
  const url = apiHost + path;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body) headers['Content-Type'] = 'application/json';
  if (authenticated) {
    const credentials = getOAuthCredentialsFromSession();
    if (!credentials)
      throw withPricingFailureContext(new PricingApiError(401, {
        title: 'Sign in required',
        message: 'Please sign in to continue.',
      }), { endpoint, failure: 'authentication' });
    headers.Authorization = await generateOAuthHeader(method, url, credentials);
  }
  // Quote/config/status/result requests have no financial effects. Bound these
  // reads; purchase mutations deliberately have no client abort timer.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  let response: Response;
  let data: unknown;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      mode: 'cors',
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    });
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  } catch (error) {
    throw withPricingFailureContext(error, {
      endpoint,
      failure: controller.signal.aborted ? 'timeout'
        : error instanceof Error && error.name === 'AbortError' ? 'aborted'
          : error instanceof TypeError ? 'network' : 'transport',
    });
  } finally {
    clearTimeout(timeout);
  }
  if (!response.ok || (isRecord(data) && data.error)) {
    const error = isRecord(data) && isRecord(data.error) ? data.error : {};
    const retryAfter = retryAfterSeconds(
      response.headers.get('Retry-After'),
      response.headers.get('Date'),
    );
    const failure = new PricingApiError(
      response.status,
      {
        title: typeof error.title === 'string' ? error.title : 'Pricing unavailable',
        message:
          typeof error.message === 'string'
            ? error.message
            : typeof error.title === 'string'
              ? error.title
              : response.status === 401
                ? 'Please sign in again to check your payment.'
                : 'We cannot confirm pricing or payment status right now. Please refresh or contact support.',
        code: typeof error.code === 'string' ? error.code : undefined,
        attempt_id: isAttemptId(error.attempt_id) ? error.attempt_id : undefined,
        replacement_quote: isPricingQuote(error.replacement_quote)
          ? error.replacement_quote
          : undefined,
      },
      retryAfter,
    );
    throw withPricingFailureContext(failure, { endpoint, failure: 'http' });
  }
  if (isRecord(data)) responseDates.set(data, response.headers.get('Date'));
  return data;
}
const invalid = (endpoint: PricingEndpoint) => {
  const error = new PricingApiError(503, {
    title: 'Pricing unavailable',
    message: 'Pricing details are temporarily unavailable. Please refresh.',
  });
  return withPricingFailureContext(error, { endpoint, failure: 'invalid_contract' });
};
export async function fetchPricingConfig(apiHost?: string): Promise<PricingConfig> {
  const data = await pricingRequest('/api/pricing/config', undefined, false, apiHost);
  if (!isPricingConfig(data)) throw invalid('/api/pricing/config');
  return data;
}
export async function fetchPricingCatalog(apiHost?: string) {
  const data = await pricingRequest('/api/pricing/catalog', undefined, false, apiHost);
  if (!isPricingCatalog(data)) throw invalid('/api/pricing/catalog');
  return data;
}
export function plansForCatalog(catalog: Awaited<ReturnType<typeof fetchPricingCatalog>>) {
  return Object.fromEntries(
    Object.entries(PLANS).map(([tier, plan]) => {
      const product = tier.startsWith('proplus') ? catalog.prices.pro_plus : catalog.prices.pro;
      const price =
        plan.billingPeriod === 'lifetime'
          ? catalog.prices.pro_plus.lifetime[1]
          : plan.billingPeriod === 'monthly'
            ? product.monthly[1]
            : product.yearly[1];
      return [
        tier,
        {
          ...plan,
          basePrice: price / 100,
          annualPricesCents: plan.supportsMultiYear
            ? Object.fromEntries(ANNUAL_TERMS.map((term) => [term, product.yearly[term]]))
            : undefined,
        },
      ];
    }),
  ) as typeof PLANS;
}
export async function fetchPricingQuote(
  target: ExecutionTarget,
  selection?: { tier: PlanTier; subscriptionLength: number; promoCode?: string },
) {
  const endpoint = QUOTE_PATHS[target];
  if (
    ['subscription_create', 'subscription_update'].includes(target) &&
    (!selection || !isValidSubscriptionLength(selection.tier, selection.subscriptionLength))
  )
    throw invalid(endpoint);
  if (['subscription_demote', 'payment_method_update'].includes(target) && selection)
    throw invalid(endpoint);
  const body: Record<string, unknown> = { quote_schema_version: 2 };
  if (selection) {
    body.membership_package_name = QUOTED_MEMBERSHIP_PACKAGES[selection.tier];
    body.subscription = selection.subscriptionLength;
    if (selection.promoCode) body.id = selection.promoCode;
  }
  const data = await pricingRequest(endpoint, body, true);
  if (!isPricingQuote(data) || data.execution_target !== target) throw invalid(endpoint);
  registerQuoteClock(data, responseDates.get(data) ?? null);
  return data;
}
export async function fetchPromoPreview(selection: {
  tier: PlanTier;
  subscriptionLength: number;
  promoCode: string;
}): Promise<PricingQuote> {
  if (!isValidSubscriptionLength(selection.tier, selection.subscriptionLength)) throw invalid('/api/pricing/promo_preview');
  const data = await pricingRequest(
    '/api/pricing/promo_preview',
    {
      quote_schema_version: 2,
      membership_package_name: QUOTED_MEMBERSHIP_PACKAGES[selection.tier],
      subscription: selection.subscriptionLength,
      promo_code: selection.promoCode,
    },
    true,
  );
  if (!isPricingQuote(data) || data.execution_target !== 'subscription_create') throw invalid('/api/pricing/promo_preview');
  registerQuoteClock(data, responseDates.get(data) ?? null);
  return data;
}
export async function fetchAttemptStatus(id: string) {
  if (!isAttemptId(id)) throw invalid('/api/pricing/attempts/:id');
  const data = await pricingRequest(
    `/api/pricing/attempts/${encodeURIComponent(id)}`,
    undefined,
    true,
  );
  if (!isAttemptStatus(data) || data.attempt_id !== id) throw invalid('/api/pricing/attempts/:id');
  return data;
}
export async function fetchAttemptResult(id: string): Promise<CompletedResult> {
  if (!isAttemptId(id)) throw invalid('/api/pricing/attempts/:id/result');
  const data = await pricingRequest(
    `/api/pricing/attempts/${encodeURIComponent(id)}/result`,
    undefined,
    true,
  );
  if (!isRecord(data) || data.attempt_id !== id || data.status !== 'completed') throw invalid('/api/pricing/attempts/:id/result');
  const purchase =
    isConfirmedPurchase(data.purchase) && data.purchase.purchase_id === id ? data.purchase : null;
  if (!purchase) void reportPricingFailure('completed_result_details', invalid('/api/pricing/attempts/:id/result'));
  return { attempt_id: id, status: 'completed', purchase };
}
export async function fetchBillingContract(): Promise<BillingContract> {
  const data = await pricingRequest('/api/pricing/billing_contract', undefined, true);
  if (!isRecord(data) || !isBillingContract(data.billing_contract)) throw invalid('/api/pricing/billing_contract');
  return data.billing_contract;
}
